/**
 * BAR-1564: an impersonation belongs to the tab that started it.
 *
 * The target's token and user, and the session metadata, live in this tab's
 * sessionStorage. localStorage keeps the admin's own session the whole time,
 * so the admin's other tabs, and tabs opened later, stay the admin, and a tab
 * closed mid-impersonation leaves nothing of the target behind.
 *
 * Before, the target's token replaced the admin's in localStorage. Every other
 * tab, and every new one, then acted as the target with no banner and no Exit,
 * and closing the impersonating tab left the browser signed in as the target.
 *
 * A reload keeps the impersonation (sessionStorage survives it). A duplicated
 * tab copies it, banner and Exit included.
 */
import type { ImpersonationMeta } from '../context/AuthContext';

const META_KEY = 'bark_impersonation';
const TOKEN_KEY = 'bark_imp_auth_token';
const USER_KEY = 'bark_imp_user_data';
// The admin stash of the first design, which kept the target in localStorage.
const LEGACY_KEYS = ['bark_real_auth_token', 'bark_real_user_data', 'bark_real_refresh_token'];

const tabStorage = (): Storage | null => {
    if (typeof window === 'undefined') return null;
    try { return window.sessionStorage; } catch { return null; }
};

/** The metadata of this tab's impersonation, or null when there is none. */
export function readImpersonationMeta(): ImpersonationMeta | null {
    const raw = tabStorage()?.getItem(META_KEY);
    if (!raw) return null;
    try {
        const meta = JSON.parse(raw);
        return meta && typeof meta === 'object' && typeof meta.session_id === 'string' ? meta : null;
    } catch {
        return null;
    }
}

/** True when this tab impersonates: metadata, token and user are all here. */
export function isImpersonatingTab(): boolean {
    const store = tabStorage();
    return Boolean(readImpersonationMeta() && store?.getItem(TOKEN_KEY) && store.getItem(USER_KEY));
}

/** The token this tab's requests carry: the impersonation's, else the browser's session. */
export function readAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    if (isImpersonatingTab()) return tabStorage()!.getItem(TOKEN_KEY);
    return window.localStorage.getItem('bark_auth_token');
}

/** The saved user (JSON) of this tab: the impersonated user's, else the browser's session. */
export function readUserData(): string | null {
    if (typeof window === 'undefined') return null;
    if (isImpersonatingTab()) return tabStorage()!.getItem(USER_KEY);
    return window.localStorage.getItem('bark_user_data');
}

/** Start impersonating in this tab. localStorage is not touched. */
export function storeImpersonation(token: string, user: unknown, meta: ImpersonationMeta): void {
    const store = tabStorage();
    if (!store) return;
    store.setItem(TOKEN_KEY, token);
    store.setItem(USER_KEY, JSON.stringify(user ?? {}));
    store.setItem(META_KEY, JSON.stringify(meta));
}

/** Save a newer copy of the signed-in user where this tab reads it from. */
export function storeUserData(user: unknown): void {
    if (typeof window === 'undefined') return;
    if (isImpersonatingTab()) tabStorage()!.setItem(USER_KEY, JSON.stringify(user));
    else window.localStorage.setItem('bark_user_data', JSON.stringify(user));
}

/** End this tab's impersonation locally (the old admin stash goes too). */
export function clearImpersonation(): void {
    const store = tabStorage();
    if (!store) return;
    for (const key of [META_KEY, TOKEN_KEY, USER_KEY, ...LEGACY_KEYS]) store.removeItem(key);
}

/**
 * True for an impersonation token: its payload carries the `imp` claim, both
 * as auth-service's own token (keycloak mode) and as a Firebase ID token.
 * Unsigned read, only to keep such a token out of localStorage.
 */
export function isImpersonationToken(token: string | null | undefined): boolean {
    const payload = token?.split('.')[1];
    if (!payload) return false;
    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const claims = JSON.parse(atob(padded));
        return Boolean(claims && typeof claims.imp === 'object' && claims.imp !== null);
    } catch {
        return false;
    }
}
